import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import LanguageSelector from './LanguageSelector';

// BackButton component (same as in other components)
const BackButton = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  return (
    <button
      onClick={() => navigate('/')}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#888',
        padding: 8,
        borderRadius: 4,
        transition: 'color 0.2s',
        textDecoration: 'none'
      }}
      onMouseOver={(e) => e.target.style.color = '#333'}
      onMouseOut={(e) => e.target.style.color = '#888'}
      title={getTranslation('back', language)}
    >
      ←
    </button>
  );
};

// Breadcrumbs component
const Breadcrumbs = () => {
  const { language } = useLanguage();
  const location = window.location.pathname;
  const segments = location.split('/').filter(seg => seg);
  
  const crumbs = segments.map(seg => {
    const decodedSeg = decodeURIComponent(seg);
    return decodedSeg;
  });
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        let displayText;
        if (idx === 0 && seg === 'mecz') {
          displayText = getTranslation('menu', language);
        } else if (seg === 'turniej-rankingowy') {
          displayText = getTranslation('turniej-rankingowy', language);
        } else if (seg === 'mecz') {
          // Skip the intermediate 'mecz' segment
          return null;
        } else {
          displayText = getTranslation(seg, language);
        }
        
        return (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>/</span>}
            {displayText}
          </span>
        );
      }).filter(Boolean)}
    </div>
  );
};

const TournamentMenu = () => {
  const { language } = useLanguage();
  
  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
      {/* Language Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>
      
      {/* Menu Container */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: window.innerWidth <= 600 ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: window.innerWidth <= 600 ? '16px' : '24px', 
          width: '100%', 
          maxWidth: '800px',
          padding: window.innerWidth <= 600 ? '0 20px' : '0'
        }}>
          <Link to="/mecz/turniej-rankingowy/dodaj" style={{
            background: 'linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%)',
            color: '#1e3a8a',
            textDecoration: 'none',
            padding: '24px 20px',
            borderRadius: '16px',
            fontWeight: '700',
            fontSize: '1.1rem',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'transform 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <span style={{ fontSize: '2rem' }}>🏆</span>
            {getTranslation('dodaj-turniej', language)}
          </Link>
          
          <Link to="/mecz/turniej-rankingowy/przeglad" style={{
            background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
            color: '#1b5e20',
            textDecoration: 'none',
            padding: '24px 20px',
            borderRadius: '16px',
            fontWeight: '700',
            fontSize: '1.1rem',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'transform 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <span style={{ fontSize: '2rem' }}>📋</span>
            {getTranslation('przeglad-turniejow', language)}
          </Link>
          
          <Link to="/mecz/turniej-rankingowy/mecze" style={{
            background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc80 100%)',
            color: '#e65100',
            textDecoration: 'none',
            padding: '24px 20px',
            borderRadius: '16px',
            fontWeight: '700',
            fontSize: '1.1rem',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'transform 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <span style={{ fontSize: '2rem' }}>⚔️</span>
            {getTranslation('przeglad-meczow-turniejowych', language)}
          </Link>
          
          <Link to="/mecz/turniej-rankingowy/statystyki-turniejow" style={{
            background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
            color: '#4a148c',
            textDecoration: 'none',
            padding: '24px 20px',
            borderRadius: '16px',
            fontWeight: '700',
            fontSize: '1.1rem',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'transform 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <span style={{ fontSize: '2rem' }}>📊</span>
            {getTranslation('statystyki-turniejow', language)}
          </Link>
          
          <Link to="/mecz/turniej-rankingowy/statystyki-meczow" style={{
            background: 'linear-gradient(135deg, #e8f5e8 0%, #a5d6a7 100%)',
            color: '#1b5e20',
            textDecoration: 'none',
            padding: '24px 20px',
            borderRadius: '16px',
            fontWeight: '700',
            fontSize: '1.1rem',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'transform 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <span style={{ fontSize: '2rem' }}>📈</span>
            {getTranslation('statystyki-meczow-turniejowych', language)}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TournamentMenu;
